import styled from 'styled-components';

import { formatDate } from './formatDate';
import { GhostButton } from './formControls';

import type { TranslationMemoryImport } from '@src/repositories/TranslationMemoryImportRepository';

type Status = 'idle' | 'loading' | 'ready' | 'error';

interface Props {
  imports: TranslationMemoryImport[];
  status: Status;
  error: string | null;
  retry: () => void;
  /** Re-run a past import through the same preview → confirm flow (OQ-b/DS6). */
  onReimport: (imp: TranslationMemoryImport) => void;
}

const langPair = (imp: TranslationMemoryImport): string => {
  const src = imp.sourceLanguage?.caption ?? imp.sourceLanguage?.value ?? '—';
  const tgt =
    imp.translationLanguage?.caption ??
    imp.translationLanguage?.value ??
    'All languages';
  return `${src} → ${tgt}`;
};

/**
 * The retained import history for a showcase (PLAT-10848 / E11, DS6) — every past
 * `translation_memory_import` with its file, language pair, targeted collections,
 * result counts, and uploader, plus a re-import action. Loading / empty / error
 * states are distinct (no silent failure).
 */
const ImportHistoryList = ({
  imports,
  status,
  error,
  retry,
  onReimport,
}: Props) => {
  return (
    <Section>
      <h3>Import history</h3>

      {status === 'loading' && imports.length === 0 && (
        <StatusText>Loading import history…</StatusText>
      )}
      {status === 'error' && (
        <StatusText role="alert">
          {error}{' '}
          <button type="button" onClick={retry}>
            Retry
          </button>
        </StatusText>
      )}
      {status === 'ready' && imports.length === 0 && (
        <StatusText>No imports yet.</StatusText>
      )}

      {imports.length > 0 && (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th>File</th>
                <th>Languages</th>
                <th>Collections</th>
                <th>Results</th>
                <th>Imported</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {imports.map(imp => (
                <tr key={imp.id}>
                  <td className="file">{imp.fileName}</td>
                  <td className="lang">{langPair(imp)}</td>
                  <td className="lang">
                    {imp.collections.length
                      ? imp.collections.map(c => c.name).join(', ')
                      : 'Default'}
                  </td>
                  <td className="lang">
                    {imp.counts.created}✓ · {imp.counts.overwritten}↻ ·{' '}
                    {imp.counts.skipped}–
                    {/* Only shown when non-zero: a partial write failure has to
                        stay visible after the results screen is dismissed
                        (PLAT-10871), but a clean import shouldn't carry a "0✗". */}
                    {imp.counts.failed > 0 && (
                      <Failed> · {imp.counts.failed}✗</Failed>
                    )}
                  </td>
                  <td className="lang">
                    {formatDate(imp.createdAt)}
                    {imp.createdByUser?.name
                      ? ` · ${imp.createdByUser.name}`
                      : ''}
                  </td>
                  <td className="actions">
                    <GhostButton type="button" onClick={() => onReimport(imp)}>
                      Re-import
                    </GhostButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}
    </Section>
  );
};

export default ImportHistoryList;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;

  > h3 {
    font-size: var(--text-base);
    font-weight: var(--font-weight-bold);
    color: var(--foreground);
    margin: 0;
  }
`;

const TableScroll = styled.div`
  overflow: auto;
  max-height: 40vh;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);

  > thead th {
    position: sticky;
    top: 0;
    background: var(--background);
    text-align: left;
    padding: 8px 12px;
    color: var(--text-muted);
    font-weight: var(--font-weight-semi-bold);
    box-shadow: inset 0 -1px 0 var(--border);
    white-space: nowrap;
  }

  > tbody td {
    padding: 8px 12px;
    border-bottom: 1px solid var(--border);
    color: var(--foreground);
    vertical-align: middle;
    overflow-wrap: anywhere;
  }

  > tbody tr:last-child td {
    border-bottom: none;
  }

  .file {
    font-weight: var(--font-weight-medium);
  }

  .lang {
    color: var(--text-secondary);
  }

  .actions {
    width: 1%;
    white-space: nowrap;
  }
`;

// Failed-row tally in the compact counts cell — tinted so a partial import is
// distinguishable at a glance from a clean one.
const Failed = styled.span`
  color: var(--destructive);
`;

const StatusText = styled.p`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin: 4px 2px;

  > button {
    background: none;
    border: none;
    color: var(--primary);
    cursor: pointer;
    font-size: inherit;
    padding: 0;
  }
`;
